import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Checkbox, Col, Row, Select, Space, Statistic, Table, Tag, Typography, message } from 'antd';
import { SyncOutlined, ReloadOutlined } from '@ant-design/icons';
import { api } from './api';

const { Text, Paragraph } = Typography;

type RegisterName = 'CVR' | 'BBR' | 'DAR';

type RegisterResult = {
  registerName: RegisterName;
  previousLastEventId: number;
  latestEventId: number;
  fetchedEventsCount: number;
  newEventsCount: number;
  reachedMaxPages: boolean;
};

type SyncResult = {
  runId: string;
  registers: RegisterName[];
  fetchedEventsCount: number;
  newEventsCount: number;
  processedEventsCount: number;
  changedCompaniesCount: number;
  createdTodosCount: number;
  refreshedWorkListsCount: number;
  changedWorkListsCount: number;
  changedWorkListItemsCount: number;
  registerResults: RegisterResult[];
};

type SyncRunLog = {
  id: string;
  started_at: string;
  finished_at: string | null;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
  registers: string[];
  fetched_events_count: number;
  new_events_count: number;
  error_message: string | null;
  created_at: string;
  duration_seconds: number | null;
};

type EventLog = {
  id: string;
  register_name: RegisterName;
  event_id: string;
  entity_name: string;
  event_action: 'i' | 'u' | 'd';
  object_id: string | null;
  processing_status: 'PENDING' | 'PROCESSED' | 'SKIPPED' | 'FAILED';
  processed_at: string | null;
  processing_error: string | null;
  datafordeler_updated_at: string | null;
  first_seen_at: string;
  change_details: {
    summary: string;
    changeType: string;
    fields: Record<string, { previous: unknown; current: unknown }>;
  };
};

type EventGroup = {
  key: string;
  register_name: RegisterName;
  object_id: string | null;
  entityNames: string[];
  events: EventLog[];
  latestProcessedAt: string | null;
  worstStatus: EventLog['processing_status'];
  bestChangeDetails: EventLog['change_details'] | null;
};

type LogResponse = {
  runs: SyncRunLog[];
  events: EventLog[];
  summaries: {
    lookbackDays: number;
    processedSampleSize: number;
    todayIngestedByRegister: Record<RegisterName, number>;
    todayProcessedByRegister: Record<RegisterName, number>;
  };
};

const emptyLogs: LogResponse = {
  runs: [],
  events: [],
  summaries: {
    lookbackDays: 7,
    processedSampleSize: 0,
    todayIngestedByRegister: { CVR: 0, BBR: 0, DAR: 0 },
    todayProcessedByRegister: { CVR: 0, BBR: 0, DAR: 0 }
  }
};

const registerOptions: { label: string; value: RegisterName }[] = [
  { label: 'CVR', value: 'CVR' },
  { label: 'BBR', value: 'BBR' },
  { label: 'DAR', value: 'DAR' }
];

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return '–';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('da-DK', {
    dateStyle: 'short',
    timeStyle: 'medium'
  }).format(date);
}

const changeFieldLabels: Record<string, string> = {
  companyForm: 'Virksomhedsform',
  companyName: 'Virksomhedsnavn',
  cvr: 'CVR-nummer',
  industry: 'Branche',
  employees: 'Antal ansatte',
  phone: 'Telefonnummer',
  email: 'E-mailadresse',
  postalAddress: 'Postadresse',
  displayAddress: 'Adresse',
  status: 'Status',
  statusLabel: 'Status',
  usage: 'Anvendelse',
  usageCode: 'Anvendelseskode',
  totalArea: 'Samlet areal',
  residentialArea: 'Boligareal',
  commercialArea: 'Erhvervsareal',
  heating: 'Opvarmning',
  roofMaterial: 'Tagmateriale',
  wallMaterial: 'Ydervægsmateriale',
  streetName: 'Vejnavn',
  houseNumber: 'Husnummer',
  postalCode: 'Postnummer',
  city: 'By',
  municipality: 'Kommune'
};

function formatChangeValue(value: unknown) {
  if (value === null || value === undefined || value === '') {
    return '–';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map(formatChangeValue).join(', ');
  }
  return JSON.stringify(value);
}

const statusRank: Record<EventLog['processing_status'], number> = { FAILED: 3, PROCESSED: 2, SKIPPED: 1, PENDING: 0 };

function groupEvents(events: EventLog[]): EventGroup[] {
  const map = new Map<string, EventGroup>();
  for (const event of events) {
    const key = `${event.register_name}__${event.object_id ?? ''}`;
    let group = map.get(key);
    if (!group) {
      group = {
        key,
        register_name: event.register_name,
        object_id: event.object_id,
        entityNames: [],
        events: [],
        latestProcessedAt: null,
        worstStatus: 'PENDING',
        bestChangeDetails: null
      };
      map.set(key, group);
    }
    group.events.push(event);
    if (!group.entityNames.includes(event.entity_name)) {
      group.entityNames.push(event.entity_name);
    }
    if (event.processed_at && (!group.latestProcessedAt || event.processed_at > group.latestProcessedAt)) {
      group.latestProcessedAt = event.processed_at;
    }
    if (statusRank[event.processing_status] > statusRank[group.worstStatus]) {
      group.worstStatus = event.processing_status;
    }
    const hasFields = event.change_details && Object.keys(event.change_details.fields || {}).length > 0;
    const groupHasFields = group.bestChangeDetails && Object.keys(group.bestChangeDetails.fields || {}).length > 0;
    if (!groupHasFields) {
      if (hasFields || !group.bestChangeDetails) {
        group.bestChangeDetails = event.change_details ?? null;
      }
    }
  }
  return Array.from(map.values());
}

function renderChangeDetails(details: EventLog['change_details']) {
  const fields = Object.entries(details?.fields || {});
  const visibleFields = fields.slice(0, 4);
  const isCreated = details?.changeType === 'i' || details?.changeType === 'new';
  return (
    <Space direction="vertical" size={2}>
      <Text strong>{details?.summary || 'Registerhændelse'}</Text>
      {visibleFields.map(([name, change]) => (
        <Text key={name} type="secondary">
          {changeFieldLabels[name] || name}: {change.previous == null && isCreated ? 'Ikke eksisterende' : formatChangeValue(change.previous)} → {formatChangeValue(change.current)}
        </Text>
      ))}
      {fields.length > visibleFields.length && (
        <Text type="secondary">+{fields.length - visibleFields.length} yderligere felter</Text>
      )}
      {!fields.length && <Text type="secondary">Datafordeler leverede ingen feltdifference.</Text>}
    </Space>
  );
}

export default function AdminDatafordelerEventsPage() {
  const [selectedRegisters, setSelectedRegisters] = useState<RegisterName[]>(['CVR', 'BBR', 'DAR']);
  const [eventRegisterFilter, setEventRegisterFilter] = useState<RegisterName[]>([]);
  const [eventStatusFilter, setEventStatusFilter] = useState<EventLog['processing_status'][]>([]);
  const [eventActionFilter, setEventActionFilter] = useState<EventLog['event_action'][]>([]);
  const [syncing, setSyncing] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logLoadError, setLogLoadError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<SyncResult | null>(null);
  const [logs, setLogs] = useState<LogResponse>(emptyLogs);

  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const res = await api('/api/admin/datafordeler-events/logs');
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke hente log');
      }
      const data = (await res.json()) as LogResponse;
      setLogs(data);
      setLogLoadError(null);
    } catch (error: any) {
      const errorMessage = error?.message || 'Kunne ikke hente Datafordeler-log';
      setLogs(emptyLogs);
      setLogLoadError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  useEffect(() => {
    void loadLogs();
  }, [loadLogs]);

  const latestRun = useMemo(() => logs.runs[0] ?? null, [logs.runs]);
  const eventDistribution = useMemo(() => {
    return logs.events.reduce(
      (acc, event) => {
        acc[event.register_name] += 1;
        return acc;
      },
      { CVR: 0, BBR: 0, DAR: 0 } as Record<RegisterName, number>
    );
  }, [logs.events]);
  const filteredEvents = useMemo(() => {
    return logs.events.filter((event) => {
      if (eventRegisterFilter.length && !eventRegisterFilter.includes(event.register_name)) {
        return false;
      }
      if (eventStatusFilter.length && !eventStatusFilter.includes(event.processing_status)) {
        return false;
      }
      if (eventActionFilter.length && !eventActionFilter.includes(event.event_action)) {
        return false;
      }
      return true;
    });
  }, [logs.events, eventRegisterFilter, eventStatusFilter, eventActionFilter]);

  const groupedEvents = useMemo(() => groupEvents(filteredEvents), [filteredEvents]);

  const [expandedGroupKeys, setExpandedGroupKeys] = useState<string[]>([]);
  useEffect(() => {
    setExpandedGroupKeys((prev) => {
      const validKeys = new Set(groupedEvents.map((g) => g.key));
      const singleKeys = groupedEvents.filter((g) => g.events.length === 1).map((g) => g.key);
      const merged = new Set([...prev.filter((k) => validKeys.has(k)), ...singleKeys]);
      return Array.from(merged);
    });
  }, [groupedEvents]);

  async function handleSync() {
    if (!selectedRegisters.length) {
      message.warning('Vælg mindst ét register');
      return;
    }

    setSyncing(true);
    try {
      const res = await api('/api/admin/datafordeler-events/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registers: selectedRegisters })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke opdatere Datafordeler-hændelser');
      }

      const data = (await res.json()) as SyncResult;
      setLastResult(data);
      message.success(
        `Datafordeler opdateret: ${data.newEventsCount} nye hændelser, ${data.changedCompaniesCount} virksomheder ændret`
      );
      await loadLogs();
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke opdatere Datafordeler-hændelser');
    } finally {
      setSyncing(false);
    }
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        title="Datafordeler"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} loading={loadingLogs} onClick={() => void loadLogs()}>
              Opdater log
            </Button>
            <Button type="primary" icon={<SyncOutlined />} loading={syncing} onClick={handleSync}>
              Start sync
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text type="secondary">
            Starter Datafordeler-sync og viser en log over seneste kørsler samt de hændelser, der blev behandlet.
          </Text>
          <Checkbox.Group
            options={registerOptions}
            value={selectedRegisters}
            onChange={(values) => setSelectedRegisters(values as RegisterName[])}
          />
        </Space>
      </Card>

      {lastResult && (
        <>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Card bordered={false}>
                <Statistic title="Hentede hændelser" value={lastResult.fetchedEventsCount} />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card bordered={false}>
                <Statistic title="Nye gemte hændelser" value={lastResult.newEventsCount} />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card bordered={false}>
                <Statistic title="Ændrede virksomheder" value={lastResult.changedCompaniesCount} />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card bordered={false}>
                <Statistic title="Hændelser behandlet" value={lastResult.processedEventsCount} />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card bordered={false}>
                <Statistic title="Oprettede todos" value={lastResult.createdTodosCount} />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card bordered={false}>
                <Statistic title="Arbejdslister fundet til kontrol" value={lastResult.refreshedWorkListsCount} />
              </Card>
            </Col>
          </Row>

          <Card title="Seneste kørsel" bodyStyle={{ padding: 0 }}>
            <Table
              rowKey="registerName"
              pagination={false}
              dataSource={lastResult.registerResults}
              columns={[
                {
                  title: 'Register',
                  dataIndex: 'registerName',
                  render: (value: RegisterName) => <Tag color="blue">{value}</Tag>
                },
                {
                  title: 'Cursor før',
                  dataIndex: 'previousLastEventId'
                },
                {
                  title: 'Cursor efter',
                  dataIndex: 'latestEventId'
                },
                {
                  title: 'Hentet',
                  dataIndex: 'fetchedEventsCount'
                },
                {
                  title: 'Nye',
                  dataIndex: 'newEventsCount'
                },
                {
                  title: 'Status',
                  render: (_: unknown, record: RegisterResult) =>
                    record.reachedMaxPages ? <Tag color="orange">Flere sider</Tag> : <Tag color="green">Færdig</Tag>
                }
              ]}
            />
          </Card>
        </>
      )}

      <Card title="Log over sync-kørsler" bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="id"
          loading={loadingLogs}
          pagination={{ pageSize: 10 }}
          dataSource={logs.runs}
          columns={[
            {
              title: 'Startet',
              dataIndex: 'started_at',
              render: (value: string) => formatDateTime(value)
            },
            {
              title: 'Afsluttet',
              dataIndex: 'finished_at',
              render: (value: string | null) => formatDateTime(value)
            },
            {
              title: 'Status',
              dataIndex: 'status',
              render: (value: SyncRunLog['status']) => {
                const color = value === 'COMPLETED' ? 'green' : value === 'FAILED' ? 'red' : 'gold';
                return <Tag color={color}>{value}</Tag>;
              }
            },
            {
              title: 'Registre',
              dataIndex: 'registers',
              render: (value: string[]) => value.join(', ')
            },
            {
              title: 'Hentet',
              dataIndex: 'fetched_events_count'
            },
            {
              title: 'Nye',
              dataIndex: 'new_events_count'
            },
            {
              title: 'Varighed (s)',
              dataIndex: 'duration_seconds',
              render: (value: number | null) => (value === null ? '–' : value.toFixed(1))
            }
          ]}
        />
      </Card>

      <Card title="Log over behandlede Datafordeler-hændelser" bodyStyle={{ padding: 0 }}>
        <div style={{ padding: 16 }}>
          {logLoadError && (
            <Paragraph type="danger" style={{ marginBottom: 8 }}>
              Loggen kunne ikke opdateres ({logLoadError}). Viste rækker er nulstillet, så gamle data ikke vises som aktuelle.
            </Paragraph>
          )}
          <Text type="secondary">
            Fordeling i tabellen nedenfor gælder de viste behandlede hændelser de seneste {logs.summaries.lookbackDays} dage ({logs.summaries.processedSampleSize} rækker).
          </Text>
          <Row gutter={16} style={{ marginTop: 12 }}>
            <Col xs={24} sm={8}>
              <Card bordered={false}>
                <Statistic
                  title={`CVR hentet seneste ${logs.summaries.lookbackDays} dage`}
                  value={logs.summaries.todayIngestedByRegister.CVR}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card bordered={false}>
                <Statistic
                  title={`BBR hentet seneste ${logs.summaries.lookbackDays} dage`}
                  value={logs.summaries.todayIngestedByRegister.BBR}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card bordered={false}>
                <Statistic
                  title={`DAR hentet seneste ${logs.summaries.lookbackDays} dage`}
                  value={logs.summaries.todayIngestedByRegister.DAR}
                />
              </Card>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Card bordered={false}>
                <Statistic title="CVR i viste loglinjer" value={eventDistribution.CVR} />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card bordered={false}>
                <Statistic title="BBR i viste loglinjer" value={eventDistribution.BBR} />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card bordered={false}>
                <Statistic title="DAR i viste loglinjer" value={eventDistribution.DAR} />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card bordered={false}>
                <Statistic
                  title={`CVR behandlet seneste ${logs.summaries.lookbackDays} dage`}
                  value={logs.summaries.todayProcessedByRegister.CVR}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card bordered={false}>
                <Statistic
                  title={`BBR behandlet seneste ${logs.summaries.lookbackDays} dage`}
                  value={logs.summaries.todayProcessedByRegister.BBR}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card bordered={false}>
                <Statistic
                  title={`DAR behandlet seneste ${logs.summaries.lookbackDays} dage`}
                  value={logs.summaries.todayProcessedByRegister.DAR}
                />
              </Card>
            </Col>
          </Row>
          <Space wrap style={{ marginTop: 12 }}>
            <Select<RegisterName>
              mode="multiple"
              allowClear
              placeholder="Filtrer register"
              value={eventRegisterFilter}
              onChange={(values) => setEventRegisterFilter(values as RegisterName[])}
              options={registerOptions}
              style={{ minWidth: 220 }}
            />
            <Select<EventLog['processing_status']>
              mode="multiple"
              allowClear
              placeholder="Filtrer status"
              value={eventStatusFilter}
              onChange={(values) => setEventStatusFilter(values as EventLog['processing_status'][])}
              options={[
                { label: 'PROCESSED', value: 'PROCESSED' },
                { label: 'SKIPPED', value: 'SKIPPED' },
                { label: 'FAILED', value: 'FAILED' },
                { label: 'PENDING', value: 'PENDING' }
              ]}
              style={{ minWidth: 220 }}
            />
            <Select<EventLog['event_action']>
              mode="multiple"
              allowClear
              placeholder="Filtrer handling"
              value={eventActionFilter}
              onChange={(values) => setEventActionFilter(values as EventLog['event_action'][])}
              options={[
                { label: 'Oprettet (i)', value: 'i' },
                { label: 'Opdateret (u)', value: 'u' },
                { label: 'Slettet (d)', value: 'd' }
              ]}
              style={{ minWidth: 180 }}
            />
          </Space>
        </div>
        <Table
          rowKey="key"
          loading={loadingLogs}
          pagination={{ pageSize: 20 }}
          dataSource={groupedEvents}
          expandable={{
            expandedRowKeys: expandedGroupKeys,
            onExpandedRowsChange: (keys) => setExpandedGroupKeys(keys as string[]),
            expandedRowRender: (group: EventGroup) => (
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={group.events}
                style={{ margin: '0 0 8px' }}
                columns={[
                  {
                    title: 'Enhed',
                    dataIndex: 'entity_name'
                  },
                  {
                    title: 'Handling',
                    dataIndex: 'event_action',
                    render: (value: EventLog['event_action'], record: EventLog) =>
                      record.change_details?.changeType === 'replaced' ? <Tag color="blue">Erstattet</Tag> : <Tag>{value}</Tag>
                  },
                  {
                    title: 'Behandlet',
                    dataIndex: 'processed_at',
                    render: (value: string | null) => formatDateTime(value)
                  },
                  {
                    title: 'Datafordeler opdateret',
                    dataIndex: 'datafordeler_updated_at',
                    render: (value: string | null) => formatDateTime(value)
                  },
                  {
                    title: 'Ændring',
                    dataIndex: 'change_details',
                    render: (value: EventLog['change_details']) => renderChangeDetails(value)
                  },
                  {
                    title: 'Status',
                    dataIndex: 'processing_status',
                    render: (value: EventLog['processing_status']) => {
                      const color = value === 'PROCESSED' ? 'green' : value === 'FAILED' ? 'red' : value === 'SKIPPED' ? 'orange' : 'default';
                      return <Tag color={color}>{value}</Tag>;
                    }
                  },
                  {
                    title: 'Bemærkning',
                    dataIndex: 'processing_error',
                    render: (value: string | null) => (value ? <Paragraph style={{ marginBottom: 0 }}>{value}</Paragraph> : '–')
                  }
                ]}
              />
            )
          }}
          columns={[
            {
              title: 'Senest behandlet',
              dataIndex: 'latestProcessedAt',
              render: (value: string | null) => formatDateTime(value)
            },
            {
              title: 'Register',
              dataIndex: 'register_name',
              render: (value: RegisterName) => <Tag color="blue">{value}</Tag>
            },
            {
              title: 'Enheder',
              dataIndex: 'entityNames',
              render: (values: string[]) => {
                const visible = values.slice(0, 3);
                const rest = values.length - visible.length;
                return (
                  <Space size={2} wrap>
                    {visible.map((name) => <Tag key={name}>{name}</Tag>)}
                    {rest > 0 && <Tag>+{rest}</Tag>}
                  </Space>
                );
              }
            },
            {
              title: 'Objekt',
              dataIndex: 'object_id',
              render: (value: string | null) => <Text code>{value || '–'}</Text>
            },
            {
              title: 'Hændelser',
              dataIndex: 'events',
              render: (events: EventLog[]) => (
                <Tag color={events.length > 1 ? 'geekblue' : 'default'}>{events.length}</Tag>
              )
            },
            {
              title: 'Vigtigste ændring',
              dataIndex: 'bestChangeDetails',
              render: (value: EventLog['change_details'] | null) =>
                value ? renderChangeDetails(value) : <Text type="secondary">–</Text>
            },
            {
              title: 'Status',
              dataIndex: 'worstStatus',
              render: (value: EventLog['processing_status']) => {
                const color = value === 'PROCESSED' ? 'green' : value === 'FAILED' ? 'red' : value === 'SKIPPED' ? 'orange' : 'default';
                return <Tag color={color}>{value}</Tag>;
              }
            }
          ]}
        />
      </Card>
      {latestRun && (
        <Text type="secondary">
          Seneste logkørsel: {formatDateTime(latestRun.started_at)} · {latestRun.status}
        </Text>
      )}
    </Space>
  );
}
